import { useEffect } from "react";
import { MapContainer, TileLayer, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import { Loader2, Upload } from "lucide-react";
import { CARTO_LIGHT, CARTO_DARK } from "../lib/mapTiles";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";

const FitAll = ({ a, b }) => {
  const map = useMap();
  useEffect(() => {
    const pts = [...(a || []), ...(b || [])];
    if (pts.length > 1) {
      const t = setTimeout(() => {
        map.invalidateSize();
        map.fitBounds(L.latLngBounds(pts), { padding: [30, 30] });
      }, 200);
      return () => clearTimeout(t);
    }
  }, [a, b, map]);
  return null;
};

export const GpxPreviewDialog = ({ preview, trip, dark, busy, onConfirm, onCancel }) => {
  if (!preview) return null;
  const oldGeo = trip.route?.geometry || [];
  const diff = Math.round(preview.distance_km - preview.current_km);
  const center = preview.geometry[Math.floor(preview.geometry.length / 2)];
  const tiles = dark ? CARTO_DARK : CARTO_LIGHT;
  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onCancel()}>
      <DialogContent className="max-w-2xl rounded-2xl" data-testid="gpx-preview-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">GPX-import controleren</DialogTitle>
          <DialogDescription>
            {preview.day != null ? `Nieuwe route voor dag ${preview.day}` : "Nieuwe route voor de hele trip"} — vergelijk met je huidige route en bevestig als dit de juiste file is.
          </DialogDescription>
        </DialogHeader>
        <div className="h-[320px] rounded-xl overflow-hidden border border-border">
          <MapContainer center={center} zoom={7} scrollWheelZoom className="w-full h-full">
            <TileLayer url={tiles} attribution='&copy; OSM &copy; CARTO' />
            <FitAll a={oldGeo} b={preview.geometry} />
            {oldGeo.length > 1 && <Polyline positions={oldGeo} pathOptions={{ color: "#9CA3AF", weight: 4, opacity: 0.7 }} />}
            <Polyline positions={preview.geometry} pathOptions={{ color: "#2563EB", weight: 5, opacity: 0.9, dashArray: "8 6" }} />
          </MapContainer>
        </div>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-xs" data-testid="gpx-preview-stats">
          <span className="flex items-center gap-2">
            <span className="inline-block w-6 h-1 rounded-full bg-[#9CA3AF]" />
            <span className="font-semibold">Huidige route</span>
            <span className="font-mono text-muted-foreground">{preview.current_km} km</span>
          </span>
          <span className="flex items-center gap-2">
            <span className="inline-block w-6 border-t-2 border-dashed border-[#2563EB]" />
            <span className="font-semibold">Nieuwe GPX-route</span>
            <span className="font-mono text-muted-foreground">
              {preview.distance_km} km{diff !== 0 && <> ({diff > 0 ? "+" : ""}{diff} km)</>}
            </span>
          </span>
        </div>
        <div className="flex justify-end gap-2">
          <Button data-testid="gpx-preview-cancel" variant="outline" className="rounded-full" onClick={onCancel} disabled={busy}>
            Annuleer
          </Button>
          <Button data-testid="gpx-preview-confirm" onClick={onConfirm} disabled={busy}
            className="rounded-full bg-primary hover:bg-[#1B4332]">
            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
            {busy ? "Importeren..." : "Importeer & herbereken laadstops"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
